import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Loader2, ChevronDown, ChevronUp, Power, Sparkles } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { WatchlistSubNav } from '@/components/watchlist/SubNav';
import { Modal, FieldLabel, TextInput, TextArea, PrimaryButton, GhostButton } from '@/components/watchlist/Modal';
import { wl } from '@/components/watchlist/theme';
import type { KeywordSetRecord, KeywordVariant, KeywordClause, KeywordTestMatchResult } from '@/types/watchlist';

// The query builder composes a real, backend-honored nested tree: a top
// implicit AND over up to 3 non-empty groups (ALL OF -> AND, ANY OF -> OR,
// NONE OF -> NOT). NEAR is shown but disabled — the live engine's
// PROXIMITY branch is identical to OR today (no token-distance check), so
// it would silently mislead rather than do what its label promises.
type GroupKey = 'allOf' | 'anyOf' | 'noneOf';
const GROUP_META: Record<GroupKey, { label: string; hint: string; op: 'AND' | 'OR' | 'NOT' }> = {
  allOf: { label: 'ALL OF', hint: 'every term must appear', op: 'AND' },
  anyOf: { label: 'ANY OF', hint: 'at least one', op: 'OR' },
  noneOf: { label: 'NONE OF', hint: 'exclude posts with these', op: 'NOT' },
};

interface FormState {
  name: string;
  platforms: string;
  isActive: boolean;
  groups: Record<GroupKey, KeywordVariant[]>;
}

const EMPTY_FORM: FormState = {
  name: '', platforms: '', isActive: true,
  groups: { allOf: [{ language_code: 'en', variant_text: '' }], anyOf: [], noneOf: [] },
};

function recordToForm(r: KeywordSetRecord): FormState {
  // Existing flat sets (built before this redesign) are read back as one
  // ALL-OF/ANY-OF/NOT-OF group matching their original top-level op — still
  // editable, just not retroactively split into groups they were never in.
  const key: GroupKey = r.query_tree.op === 'AND' ? 'allOf' : r.query_tree.op === 'NOT' ? 'noneOf' : 'anyOf';
  const groups: Record<GroupKey, KeywordVariant[]> = { allOf: [], anyOf: [], noneOf: [] };
  groups[key] = r.variants.length ? r.variants : [{ language_code: 'en', variant_text: '' }];
  return { name: r.name, platforms: r.platforms.join(', '), isActive: r.is_active, groups };
}

function buildQueryTree(groups: Record<GroupKey, KeywordVariant[]>): { tree: KeywordClause; variants: KeywordVariant[] } {
  const children: KeywordClause[] = [];
  const allVariants: KeywordVariant[] = [];
  (Object.keys(GROUP_META) as GroupKey[]).forEach((key) => {
    const clean = groups[key].filter((v) => v.variant_text.trim());
    if (clean.length === 0) return;
    allVariants.push(...clean);
    children.push({ op: GROUP_META[key].op, terms: clean.map((v) => v.variant_text), children: [] });
  });
  return { tree: { op: 'AND', terms: [], children }, variants: allVariants };
}

function GroupEditor({ groupKey, variants, onChange }: { groupKey: GroupKey; variants: KeywordVariant[]; onChange: (v: KeywordVariant[]) => void }) {
  const meta = GROUP_META[groupKey];
  return (
    <div style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, borderRadius: 8, padding: '12px 14px' }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 rounded font-bold" style={{ background: wl.greenWash, color: wl.green, fontSize: 10.5 }}>{meta.label}</span>
        <span style={{ color: wl.textFaint, fontSize: 11 }}>{meta.hint}</span>
      </div>
      <div className="flex flex-col gap-2">
        {variants.map((v, i) => (
          <div key={i} className="flex gap-2 items-center">
            <TextInput style={{ width: 60 }} value={v.language_code} onChange={(e) => onChange(variants.map((x, idx) => idx === i ? { ...x, language_code: e.target.value } : x))} placeholder="en" />
            <TextInput style={{ width: 70 }} value={v.script ?? ''} onChange={(e) => onChange(variants.map((x, idx) => idx === i ? { ...x, script: e.target.value || undefined } : x))} placeholder="script" />
            <TextInput style={{ flex: 1 }} value={v.variant_text} onChange={(e) => onChange(variants.map((x, idx) => idx === i ? { ...x, variant_text: e.target.value } : x))} placeholder="term" />
            <button onClick={() => onChange(variants.filter((_, idx) => idx !== i))} style={{ color: wl.textMuted }}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button onClick={() => onChange([...variants, { language_code: 'en', variant_text: '' }])} className="flex items-center gap-1.5 mt-2" style={{ color: wl.green, fontSize: 11.5 }}>
        <Plus size={12} /> Add term
      </button>
    </div>
  );
}

function CreateEditModal({ initial, onClose, onSaved }: { initial?: KeywordSetRecord; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(initial ? recordToForm(initial) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sampleText, setSampleText] = useState('');
  const [testResult, setTestResult] = useState<KeywordTestMatchResult | null>(null);
  const [testing, setTesting] = useState(false);

  const { tree, variants } = useMemo(() => buildQueryTree(form.groups), [form.groups]);
  const compiledPreview = useMemo(() => {
    const parts: string[] = [];
    (Object.keys(GROUP_META) as GroupKey[]).forEach((key) => {
      const clean = form.groups[key].filter((v) => v.variant_text.trim());
      if (clean.length === 0) return;
      const terms = clean.map((v) => `"${v.variant_text}"`);
      if (GROUP_META[key].op === 'NOT') parts.push(`NOT (${terms.join(' OR ')})`);
      else if (GROUP_META[key].op === 'OR') parts.push(terms.length > 1 ? `(${terms.join(' OR ')})` : terms[0]);
      else parts.push(terms.length > 1 ? `(${terms.join(' AND ')})` : terms[0]);
    });
    return parts.length ? parts.join(' AND ') : '(empty — matches nothing)';
  }, [form.groups]);

  async function runTest() {
    if (!sampleText.trim()) return;
    setTesting(true);
    try {
      const res = await watchlistApi.testKeywordSetMatch({ query_tree: tree, variants, sample_text: sampleText });
      setTestResult(res);
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!form.name.trim() || variants.length === 0) {
      setError('Name and at least one non-empty term (in any group) are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        query_tree: tree,
        variants,
        platforms: form.platforms.split(',').map((p) => p.trim()).filter(Boolean),
        is_active: form.isActive,
      };
      if (initial) await watchlistApi.updateKeywordSet(initial.id, body);
      else await watchlistApi.createKeywordSet(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={initial ? 'Edit Keyword Set' : 'New Keyword Set'} onClose={onClose} maxWidth={760}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <FieldLabel>Name</FieldLabel>
            <TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Bank name + scam terms" />
          </div>
          <div className="flex-1">
            <FieldLabel>Platforms (comma-separated, blank = all)</FieldLabel>
            <TextInput value={form.platforms} onChange={(e) => setForm((f) => ({ ...f, platforms: e.target.value }))} placeholder="twitter, telegram" />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <FieldLabel>Query</FieldLabel>
          <button disabled title="Coming soon — AI-suggested transliterations/misspellings per language" className="flex items-center gap-1.5" style={{ color: wl.textFaint, fontSize: 11 }}>
            <Sparkles size={12} /> Suggest variants with AI
          </button>
        </div>
        <GroupEditor groupKey="allOf" variants={form.groups.allOf} onChange={(v) => setForm((f) => ({ ...f, groups: { ...f.groups, allOf: v } }))} />
        <GroupEditor groupKey="anyOf" variants={form.groups.anyOf} onChange={(v) => setForm((f) => ({ ...f, groups: { ...f.groups, anyOf: v } }))} />
        <div style={{ background: wl.bg, border: `1px dashed ${wl.borderSoft}`, borderRadius: 8, padding: '12px 14px', opacity: 0.55 }}>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded font-bold" style={{ background: wl.border, color: wl.textMuted, fontSize: 10.5 }}>NEAR</span>
            <span style={{ color: wl.textFaint, fontSize: 11 }}>appears close to the brand name — coming soon (no token-distance check yet)</span>
          </div>
        </div>
        <GroupEditor groupKey="noneOf" variants={form.groups.noneOf} onChange={(v) => setForm((f) => ({ ...f, groups: { ...f.groups, noneOf: v } }))} />

        <div style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, borderRadius: 8, padding: '10px 14px' }}>
          <p style={{ color: wl.textFaint, fontSize: 10.5, marginBottom: 4 }}>COMPILED QUERY</p>
          <p style={{ color: wl.greenDim, fontSize: 12, fontFamily: wl.font }}>{compiledPreview}</p>
        </div>

        <div style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, borderRadius: 8, padding: '12px 14px' }}>
          <p style={{ color: wl.textFaint, fontSize: 10.5, marginBottom: 6 }}>TEST AGAINST A SAMPLE POST (approximate preview, not the live percolator)</p>
          <TextArea value={sampleText} onChange={(e) => setSampleText(e.target.value)} placeholder="Paste a sample post to check…" style={{ minHeight: 50 }} />
          <div className="flex items-center gap-3 mt-2">
            <GhostButton onClick={runTest}>{testing ? 'Checking…' : 'Run test'}</GhostButton>
            {testResult && (
              <span style={{ color: testResult.matched ? wl.green : wl.textFaint, fontSize: 12, fontWeight: 700 }}>
                {testResult.matched ? `MATCH · ${testResult.matched_terms.join(', ')}` : 'No match'}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
          <span style={{ color: wl.textMuted, fontSize: 12 }}>Active</span>
        </div>

        {error && <p style={{ color: wl.critical, fontSize: 12 }}>{error}</p>}

        <div className="flex justify-end gap-2 mt-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}

function HitsPanel({ keywordSetId }: { keywordSetId: string }) {
  const hitsQuery = useQuery({ queryKey: ['watchlist-hits', keywordSetId], queryFn: () => watchlistApi.listKeywordSetHits(keywordSetId) });
  if (hitsQuery.isLoading) return <Loader2 className="animate-spin" size={16} style={{ color: wl.green }} />;
  const hits = hitsQuery.data?.hits ?? [];
  if (hits.length === 0) return <p style={{ color: wl.textFaint, fontSize: 12 }}>No detections yet.</p>;
  return (
    <div className="flex flex-col gap-2">
      {hits.map((h) => (
        <div key={h.id} className="py-2" style={{ borderBottom: `1px solid ${wl.borderSoft}` }}>
          <div className="flex justify-between items-center">
            <span style={{ color: wl.textMuted, fontSize: 11, textTransform: 'capitalize' }}>{h.platform}</span>
            <div className="flex items-center gap-2">
              <span style={{ color: wl.textFaint, fontSize: 10.5 }}>{new Date(h.detected_at).toLocaleString()}</span>
              {h.post_url && <a href={h.post_url} target="_blank" rel="noreferrer" style={{ color: wl.green, fontSize: 11 }}>View post →</a>}
            </div>
          </div>
          <p style={{ color: wl.text, fontSize: 12.5, marginTop: 2 }}>{h.post_text_snippet}</p>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            {h.matched_terms.map((t) => (
              <span key={t} style={{ background: wl.greenWash, color: wl.green, fontSize: 10, padding: '1px 6px', borderRadius: 6 }}>{t}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function KeywordsPage() {
  const qc = useQueryClient();
  const [modalFor, setModalFor] = useState<'new' | KeywordSetRecord | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['watchlist-keyword-sets'], queryFn: () => watchlistApi.listKeywordSets() });

  const deleteMut = useMutation({
    mutationFn: (id: string) => watchlistApi.deleteKeywordSet(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist-keyword-sets'] }),
  });
  const toggleMut = useMutation({
    mutationFn: (set: KeywordSetRecord) => watchlistApi.updateKeywordSet(set.id, { is_active: !set.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist-keyword-sets'] }),
  });

  function refetch() {
    qc.invalidateQueries({ queryKey: ['watchlist-keyword-sets'] });
  }

  return (
    <div style={{ background: wl.bg, minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WatchlistSubNav />
      {modalFor && (
        <CreateEditModal
          initial={modalFor === 'new' ? undefined : modalFor}
          onClose={() => setModalFor(null)}
          onSaved={refetch}
        />
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 style={{ color: wl.text, fontWeight: 800, fontSize: 16 }}>Keyword sets</h1>
          <p style={{ color: wl.textMuted, fontSize: 12, marginTop: 2 }}>Multi-language percolator monitoring across every platform</p>
        </div>
        <PrimaryButton onClick={() => setModalFor('new')}>+ New keyword set</PrimaryButton>
      </div>

      {query.isLoading ? (
        <Loader2 className="animate-spin" size={22} style={{ color: wl.green }} />
      ) : (query.data?.keyword_sets.length ?? 0) === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 13 }}>No keyword sets yet — create one to start monitoring.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {query.data!.keyword_sets.map((set) => (
            <div key={set.id} style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10 }}>
              <div className="flex items-center justify-between px-5 py-4">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setExpanded(expanded === set.id ? null : set.id)}>
                  <div className="flex items-center gap-2">
                    <span style={{ color: wl.text, fontWeight: 700, fontSize: 14 }}>{set.name}</span>
                    <span style={{ background: set.is_active ? wl.greenWash : `${wl.textFaint}18`, color: set.is_active ? wl.greenDim : wl.textFaint, fontSize: 10, padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
                      {set.is_active ? 'ACTIVE' : 'PAUSED'}
                    </span>
                  </div>
                  <p style={{ color: wl.textMuted, fontSize: 11.5, marginTop: 3 }}>
                    {set.variants.length} variant{set.variants.length !== 1 ? 's' : ''} ·{' '}
                    {set.platforms.length ? set.platforms.join(', ') : 'all platforms'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => toggleMut.mutate(set)} title={set.is_active ? 'Pause' : 'Activate'} style={{ color: set.is_active ? wl.greenDim : wl.textFaint }}>
                    <Power size={15} />
                  </button>
                  <GhostButton onClick={() => setModalFor(set)}>Edit</GhostButton>
                  <GhostButton destructive onClick={() => confirm(`Delete "${set.name}"?`) && deleteMut.mutate(set.id)}>
                    <Trash2 size={13} />
                  </GhostButton>
                  <button onClick={() => setExpanded(expanded === set.id ? null : set.id)} style={{ color: wl.textMuted }}>
                    {expanded === set.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              {expanded === set.id && (
                <div className="px-5 pb-4" style={{ borderTop: `1px solid ${wl.border}` }}>
                  <p style={{ color: wl.textFaint, fontSize: 11, letterSpacing: '0.06em', fontWeight: 700, margin: '12px 0 8px' }}>RECENT HITS</p>
                  <HitsPanel keywordSetId={set.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
